/* eslint-disable @typescript-eslint/no-explicit-any -- Generic mock data store requires any for dynamic collections */
import { useState, useEffect, useCallback } from 'react';
import mockData from '../services/mockData';

const STORAGE_KEY = 'aivilo_lens_demo_data';
const STORAGE_VERSION = '1.5';

type MockStore = Record<string, any> | null;
type StoreUpdater = (prevStore: Record<string, any>) => Record<string, any>;

interface UseMockDataStoreReturn {
  // State
  store: MockStore;
  isReady: boolean;
  // Core operations
  get: (collection: string) => any[];
  getById: (collection: string, id: string) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  create: (collection: string, item: Record<string, any>) => Record<string, any> | null;
  update: (collection: string, id: string, updates: Record<string, any>) => Record<string, any> | null;
  remove: (collection: string, id: string) => boolean;
  query: (collection: string, predicate: (item: any) => boolean) => any[];
  count: (collection: string, predicate?: ((item: any) => boolean) | null) => number;
  // Store management
  clearStore: () => void;
  resetStore: () => void;
  // Raw access for complex operations
  updateStore: (updater: StoreUpdater | Record<string, any>) => void;
}

/**
 * Hook to manage mock data in browser storage
 * Provides CRUD operations that persist in localStorage
 * Data is scoped per user session and cleared on logout
 */
export const useMockDataStore = (): UseMockDataStoreReturn => {
  const [store, setStore] = useState<MockStore>(null);

  // Initialize store from localStorage or defaults
  useEffect(() => {
    const initializeStore = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.version === STORAGE_VERSION) {
            setStore(parsed.data);
            return;
          }
        }
      } catch (error) {
      }

      // Initialize with default mock data
      const initialData = JSON.parse(JSON.stringify(mockData));
      setStore(initialData);
      saveToStorage(initialData);
    };

    initializeStore();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save to localStorage
  const saveToStorage = useCallback((data: Record<string, any>) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        data,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
    }
  }, []);

  // Update store and persist
  const updateStore = useCallback((updater: StoreUpdater | Record<string, any>) => {
    setStore(prevStore => {
      const newStore = typeof updater === 'function' ? updater(prevStore as Record<string, any>) : updater;
      saveToStorage(newStore);
      return newStore;
    });
  }, [saveToStorage]);

  // Clear all mock data
  const clearStore = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setStore(null);
    } catch (error) {
    }
  }, []);

  // Reset to default mock data
  const resetStore = useCallback(() => {
    const initialData = JSON.parse(JSON.stringify(mockData));
    setStore(initialData);
    saveToStorage(initialData);
  }, [saveToStorage]);

  // Generic CRUD operations
  const get = useCallback((collection: string): any[] => {
    if (!store?.[collection]) return [];
    return store[collection];
  }, [store]);

  const getById = useCallback((collection: string, id: string): any => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!store?.[collection]) return null;
    const items: Record<string, unknown>[] = Array.isArray(store[collection]) ? store[collection] : [store[collection]];
    return items.find(item => item.id === id) || null;
  }, [store]);

  const create = useCallback((collection: string, item: Record<string, any>): Record<string, any> | null => {
    if (!store) return null;

    const newItem = {
      ...item,
      id: item.id || crypto.randomUUID(),
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || new Date().toISOString()
    };

    updateStore(prevStore => ({
      ...prevStore,
      [collection]: Array.isArray(prevStore[collection])
        ? [...prevStore[collection], newItem]
        : newItem
    }));

    return newItem;
  }, [store, updateStore]);

  const update = useCallback((collection: string, id: string, updates: Record<string, any>): Record<string, any> | null => {
    if (!store) return null;

    let updatedItem: Record<string, any> | null = null;

    updateStore(prevStore => {
      const items: Record<string, unknown>[] = Array.isArray(prevStore[collection]) ? prevStore[collection] : [prevStore[collection]];
      const index = items.findIndex(item => item.id === id);

      if (index === -1) return prevStore;

      updatedItem = {
        ...items[index],
        ...updates,
        id, // Preserve ID
        updated_at: new Date().toISOString()
      };

      const newItems = [...items];
      newItems[index] = updatedItem;

      return {
        ...prevStore,
        [collection]: Array.isArray(prevStore[collection]) ? newItems : updatedItem
      };
    });

    return updatedItem;
  }, [store, updateStore]);

  const remove = useCallback((collection: string, id: string): boolean => {
    if (!store) return false;

    let removed = false;

    updateStore(prevStore => {
      const items: Record<string, unknown>[] = Array.isArray(prevStore[collection]) ? prevStore[collection] : [prevStore[collection]];
      const index = items.findIndex(item => item.id === id);

      if (index === -1) return prevStore;

      removed = true;
      const newItems = items.filter(item => item.id !== id);

      return {
        ...prevStore,
        [collection]: newItems
      };
    });

    return removed;
  }, [store, updateStore]);

  // Query helpers
  const query = useCallback((collection: string, predicate: (item: any) => boolean): any[] => {
    if (!store?.[collection]) return [];
    const items: Record<string, unknown>[] = Array.isArray(store[collection]) ? store[collection] : [store[collection]];
    return items.filter(predicate);
  }, [store]);

  const count = useCallback((collection: string, predicate: ((item: any) => boolean) | null = null): number => {
    if (!store?.[collection]) return 0;
    const items: Record<string, unknown>[] = Array.isArray(store[collection]) ? store[collection] : [store[collection]];
    return predicate ? items.filter(predicate).length : items.length;
  }, [store]);

  return {
    // State
    store,
    isReady: store !== null,

    // Core operations
    get,
    getById,
    create,
    update,
    remove,
    query,
    count,

    // Store management
    clearStore,
    resetStore,

    // Raw access for complex operations
    updateStore
  };
};

export default useMockDataStore;
